import { useEffect, useState } from "react";
import { Drawer, useToast } from "@/components/system";
import {
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  FormInput,
  FormSelect,
} from "@/components/forms";
import {
  ACTION_CATEGORIES,
  ACTION_STATES,
  type ActionItem,
  type ActionCategory,
  type ActionState,
  useActionItem,
  useUpdateActionItem,
} from "@/hooks/use-action-items";
import { formatError } from "@/lib/format-error";

/**
 * BW19 PR-E — task detail drawer.
 *
 * Inline editor for state, category, due_at, scheduled_at, and effort
 * estimate. Shows the source capture link when present. The
 * research_results section is intentionally a placeholder; BW20 wires the
 * Apify enrichment results in.
 */

export function TaskDetailDrawer({
  taskId,
  onClose,
}: {
  taskId: string | null;
  onClose: () => void;
}) {
  const open = taskId != null;
  const { data: item } = useActionItem(taskId ?? undefined);
  const update = useUpdateActionItem();
  const toast = useToast();

  const [draft, setDraft] = useState<ActionItem | null>(null);

  useEffect(() => {
    setDraft(item ?? null);
  }, [item]);

  async function save() {
    if (!draft || !taskId) return;
    try {
      await update.mutateAsync({
        id: taskId,
        patch: {
          state: draft.state,
          title: draft.title,
          description: draft.description,
          category: draft.category,
          blocker_reason: draft.blocker_reason,
          effort_estimate_min: draft.effort_estimate_min,
          due_at: draft.due_at,
          scheduled_at: draft.scheduled_at,
        },
      });
      toast.show("Saved.");
      onClose();
    } catch (err) {
      toast.show(`Save failed: ${formatError(err)}`);
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Task" size="md">
      {draft ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <FormInput
            label="Title"
            value={draft.title}
            onChange={(v) => setDraft({ ...draft, title: v })}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "var(--space-2)",
            }}
          >
            <FormSelect
              label="State"
              value={draft.state}
              onChange={(v) => setDraft({ ...draft, state: v as ActionState })}
              options={ACTION_STATES.map((s) => ({ value: s, label: s }))}
            />
            <FormSelect
              label="Category"
              value={draft.category}
              onChange={(v) => setDraft({ ...draft, category: v as ActionCategory })}
              options={ACTION_CATEGORIES.map((c) => ({ value: c, label: c }))}
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "var(--space-2)",
            }}
          >
            <FormInput
              label="Due"
              type="datetime-local"
              value={toLocalInput(draft.due_at)}
              onChange={(v) =>
                setDraft({ ...draft, due_at: v ? new Date(v).toISOString() : null })
              }
            />
            <FormInput
              label="Scheduled"
              type="datetime-local"
              value={toLocalInput(draft.scheduled_at)}
              onChange={(v) =>
                setDraft({
                  ...draft,
                  scheduled_at: v ? new Date(v).toISOString() : null,
                })
              }
            />
          </div>
          <FormInput
            label="Effort estimate (min)"
            type="number"
            value={draft.effort_estimate_min != null ? String(draft.effort_estimate_min) : ""}
            onChange={(v) =>
              setDraft({
                ...draft,
                effort_estimate_min: v === "" ? null : Number(v),
              })
            }
          />
          {draft.state === "blocked" && (
            <FormInput
              label="Blocker reason"
              value={draft.blocker_reason ?? ""}
              onChange={(v) => setDraft({ ...draft, blocker_reason: v || null })}
            />
          )}
          {draft.capture_id && (
            <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 12 }}>
              Source capture: {draft.capture_id.slice(0, 8)}...
            </p>
          )}

          <section
            aria-label="Research results"
            style={{
              padding: "var(--space-3)",
              background: "var(--bg-2)",
              border: "1px solid var(--hair)",
              borderRadius: "var(--radius-card)",
              fontSize: 13,
              color: "var(--fg-2)",
            }}
          >
            State: research drawer empty.
            <br />
            Meaning: BW20 will populate this with Apify-enriched options.
            <br />
            Next move: nothing here yet; come back after BW20-H.
          </section>

          <DialogFooter>
            <SecondaryButton onClick={onClose}>Close</SecondaryButton>
            <PrimaryButton onClick={() => void save()}>Save</PrimaryButton>
          </DialogFooter>
        </div>
      ) : (
        <p style={{ color: "var(--fg-2)" }}>Loading task...</p>
      )}
    </Drawer>
  );
}

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
